import React, { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  CheckSquare, Square, Plus, X, ChevronDown, ChevronRight, Loader2,
} from 'lucide-react';
import { loggedMutation, apiClient } from './loggedFetch';

/*
 * StepChecklist — checkable sub-tasks under a kanban card.
 *
 * Steps are seeded automatically from the Opus briefing's
 * recommended_next_steps[] when the report lands; the user can also
 * add manual steps and edit / check off any of them. Progress survives
 * Regenerate because the backend skips auto-seed when auto-source
 * steps already exist.
 *
 * Receives `steps` directly as a prop (already bundled in the plan
 * payload — no per-card fetch). All mutations invalidate the parent
 * plan query so the kanban + rollup stay in sync.
 *
 * `compact` mode is for the kanban card (collapsed by default with a
 * progress badge). `compact={false}` is for the rollup where steps
 * are always visible.
 */

function StepChecklist({
  vendor,
  cardId,
  steps = [],
  stepsDone = 0,
  stepsTotal = 0,
  compact = true,
  defaultOpen = false,
  readOnly = false,
}) {
  const [open, setOpen] = useState(defaultOpen || !compact);
  const [adding, setAdding] = useState(false);
  const [draftText, setDraftText] = useState('');
  const queryClient = useQueryClient();

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: ['vendor-analysis', 'plan', vendor] });
    queryClient.invalidateQueries({ queryKey: ['vendor-analysis', 'plan-with-reports', vendor] });
  };

  const toggleStep = useMutation({
    mutationFn: loggedMutation(
      'StepChecklist:toggle',
      ({ stepId, status }) =>
        apiClient.put(
          `/api/vendor-analysis/${encodeURIComponent(vendor)}/plan/cards/${cardId}/steps/${stepId}`,
          { status }
        )
    ),
    onMutate: async ({ stepId, status }) => {
      // Optimistic — flip the checkbox immediately so click feels snappy.
      const keys = [
        ['vendor-analysis', 'plan', vendor],
        ['vendor-analysis', 'plan-with-reports', vendor],
      ];
      const previous = keys.map(k => [k, queryClient.getQueryData(k)]);
      for (const [key, prev] of previous) {
        if (!prev) continue;
        queryClient.setQueryData(key, {
          ...prev,
          cards: prev.cards.map(c => {
            if (c.id !== cardId) return c;
            const nextSteps = (c.steps || []).map(s =>
              s.id === stepId ? { ...s, status } : s
            );
            const done = nextSteps.filter(s => s.status === 'done').length;
            return { ...c, steps: nextSteps, steps_done: done, steps_total: nextSteps.length };
          }),
        });
      }
      return { previous };
    },
    onError: (_err, _vars, ctx) => {
      // Roll back optimistic update on failure.
      ctx?.previous?.forEach(([key, prev]) => prev && queryClient.setQueryData(key, prev));
    },
    onSettled: invalidate,
  });

  const addStep = useMutation({
    mutationFn: loggedMutation(
      'StepChecklist:add',
      (body) =>
        apiClient.post(
          `/api/vendor-analysis/${encodeURIComponent(vendor)}/plan/cards/${cardId}/steps`,
          body
        )
    ),
    onSuccess: () => {
      setDraftText('');
      setAdding(false);
      invalidate();
    },
  });

  const removeStep = useMutation({
    mutationFn: loggedMutation(
      'StepChecklist:remove',
      (stepId) =>
        apiClient.del(
          `/api/vendor-analysis/${encodeURIComponent(vendor)}/plan/cards/${cardId}/steps/${stepId}`
        )
    ),
    onSuccess: invalidate,
  });

  const handleAdd = () => {
    const text = draftText.trim();
    if (!text) {
      setAdding(false);
      setDraftText('');
      return;
    }
    addStep.mutate({ step_text: text });
  };

  const total = stepsTotal || steps.length;
  const done = stepsDone ?? steps.filter(s => s.status === 'done').length;

  if (compact && total === 0 && readOnly) return null;

  return (
    <div className="vca-steps">
      {compact && (
        <button
          type="button"
          className="vca-steps__toggle"
          onPointerDown={(e) => e.stopPropagation()}
          onClick={() => setOpen(o => !o)}
        >
          {open ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
          <span>Next steps</span>
          {total > 0 && (
            <span className={`vca-steps__progress ${done === total ? 'is-complete' : ''}`}>
              {done}/{total}
            </span>
          )}
        </button>
      )}

      {(!compact || open) && (
        <ul className="vca-steps__list">
          {steps.length === 0 && (
            <li className="vca-steps__empty">
              {readOnly
                ? 'No next steps yet.'
                : 'No steps yet — generate the briefing or add one manually.'}
            </li>
          )}
          {steps.map(step => (
            <StepItem
              key={step.id}
              step={step}
              onToggle={() => toggleStep.mutate({
                stepId: step.id,
                status: step.status === 'done' ? 'pending' : 'done',
              })}
              onRemove={() => removeStep.mutate(step.id)}
              isToggling={toggleStep.isPending && toggleStep.variables?.stepId === step.id}
              readOnly={readOnly}
            />
          ))}
        </ul>
      )}

      {(!compact || open) && !readOnly && (
        <div className="vca-steps__add">
          {adding ? (
            <div className="vca-steps__add-row">
              <input
                autoFocus
                value={draftText}
                onChange={(e) => setDraftText(e.target.value)}
                placeholder="What's the next concrete action?"
                onKeyDown={(e) => {
                  if (e.key === 'Enter') handleAdd();
                  if (e.key === 'Escape') { setAdding(false); setDraftText(''); }
                }}
                onPointerDown={(e) => e.stopPropagation()}
                className="vca-steps__add-input"
              />
              <button
                type="button"
                className="vca-add-btn"
                onClick={handleAdd}
                disabled={addStep.isPending}
              >
                {addStep.isPending ? <Loader2 size={12} className="vca-spin" /> : 'Add'}
              </button>
              <button
                type="button"
                className="vca-card__delete"
                onClick={() => { setAdding(false); setDraftText(''); }}
              >
                Cancel
              </button>
            </div>
          ) : (
            <button
              type="button"
              className="vca-steps__add-btn"
              onPointerDown={(e) => e.stopPropagation()}
              onClick={() => setAdding(true)}
            >
              <Plus size={12} /> Add step
            </button>
          )}
        </div>
      )}
    </div>
  );
}

function StepItem({ step, onToggle, onRemove, isToggling, readOnly }) {
  const isDone = step.status === 'done';
  return (
    <li className={`vca-step ${isDone ? 'is-done' : ''} ${step.source === 'auto' ? 'is-auto' : 'is-manual'}`}>
      <button
        type="button"
        className="vca-step__check"
        onPointerDown={(e) => e.stopPropagation()}
        onClick={onToggle}
        disabled={readOnly || isToggling}
        aria-pressed={isDone}
      >
        {isToggling ? (
          <Loader2 size={14} className="vca-spin" />
        ) : isDone ? (
          <CheckSquare size={14} />
        ) : (
          <Square size={14} />
        )}
      </button>
      <div className="vca-step__body">
        <div className="vca-step__text">{step.step_text}</div>
        {(step.owner || step.outcome) && (
          <div className="vca-step__meta">
            {step.owner && <span className="vca-step__owner">{step.owner}</span>}
            {step.outcome && <span className="vca-step__outcome">{step.outcome}</span>}
          </div>
        )}
      </div>
      {!readOnly && (
        <button
          type="button"
          className="vca-step__remove"
          onPointerDown={(e) => e.stopPropagation()}
          onClick={onRemove}
          aria-label="Remove step"
          title="Remove step"
        >
          <X size={12} />
        </button>
      )}
    </li>
  );
}

export default StepChecklist;
